/**
 * The graph's colours, resolved from the app's design tokens.
 *
 * ## Why the tokens are resolved to numbers rather than used directly
 *
 * Canvas needs a concrete colour, and it needs one it can vary the alpha of thousands of times
 * a frame. Our tokens are `oklch(...)`, and while a canvas will accept that string, building
 * `oklch(... / 0.3)` per node per frame means the browser re-parses a colour on every draw
 * call. So each token is rasterised ONCE into a 1×1 canvas and read back as RGB, and every
 * later use is a cheap `rgba()` template. It also means we never have to hand-maintain a
 * second copy of the palette that can drift from the stylesheet.
 *
 * ## The threat ramp is the app's severity ramp, unchanged
 *
 * `--severity-low|medium|high|critical` was already built and validated for exactly this
 * problem — an ordered ramp legible over a busy, dark background, separated by chroma and hue
 * rather than by lightness alone. Threat is an ordered quantity, so it uses that ramp and
 * nothing else. Node radius grows with threat as well, so the ordering survives greyscale and
 * colour-blind vision without depending on hue at all.
 *
 * ## Unread is grey, and that is a deliberate exception
 *
 * A thread nobody has assessed is drawn in the muted foreground, outside the ramp entirely. It
 * must not be able to pass as "low threat" — the failure this whole board exists to prevent is
 * a hostile thread reading as harmless because nothing ever looked at it.
 */

export interface Rgb { r: number, g: number, b: number }

export interface Palette {
  /** The console surface. Deep, and the same in both themes — see the note in the page. */
  stage: Rgb
  stageEdge: Rgb
  /** Chrome: rings, ticks, the sweep. The app's accent. */
  accent: Rgb
  /** Platform ring nodes. */
  channel: Rgb
  ink: Rgb
  inkDim: Rgb
  inkFaint: Rgb
  line: Rgb
  /** Threat ramp, low → critical. */
  threat: [Rgb, Rgb, Rgb, Rgb]
  /** Nobody has read it. Outside the ramp on purpose. */
  unread: Rgb
  /** Coordinated narratives — a pattern in the text, never a claim about a person. */
  coordinated: Rgb
  supportive: Rgb
}

function parse(value: string, fallback: Rgb): Rgb {
  if (!value) return fallback
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return fallback
  ctx.fillStyle = '#000'
  // An unparseable value leaves fillStyle at the previous colour rather than throwing, so the
  // black primer above is what turns "the token is missing" into a visible, debuggable result
  // instead of a silent inheritance of whatever was set last.
  ctx.fillStyle = value.trim()
  ctx.fillRect(0, 0, 1, 1)
  try {
    const data = ctx.getImageData(0, 0, 1, 1).data
    return { r: data[0] ?? 0, g: data[1] ?? 0, b: data[2] ?? 0 }
  } catch {
    return fallback
  }
}

const FALLBACK: Palette = {
  stage: { r: 18, g: 15, b: 13 },
  stageEdge: { r: 30, g: 24, b: 20 },
  accent: { r: 189, g: 142, b: 105 },
  channel: { r: 57, g: 135, b: 229 },
  ink: { r: 236, g: 232, b: 227 },
  inkDim: { r: 168, g: 160, b: 152 },
  inkFaint: { r: 112, g: 105, b: 99 },
  line: { r: 70, g: 62, b: 56 },
  threat: [
    { r: 255, g: 221, b: 51 },
    { r: 255, g: 154, b: 43 },
    { r: 240, g: 68, b: 85 },
    { r: 161, g: 24, b: 39 },
  ],
  unread: { r: 138, g: 132, b: 126 },
  coordinated: { r: 144, g: 133, b: 233 },
  supportive: { r: 25, g: 158, b: 112 },
}

/**
 * Read the palette off a live element.
 *
 * Pass the stage element, not `document.documentElement`: the stage carries its own scoped
 * token block, and resolving against the root would return the page's light-mode values for a
 * surface that is deliberately dark in both themes.
 */
export function readPalette(el: HTMLElement): Palette {
  const cs = getComputedStyle(el)
  const token = (name: string, fallback: Rgb) => parse(cs.getPropertyValue(name), fallback)
  return {
    stage: token('--graph-stage', FALLBACK.stage),
    stageEdge: token('--graph-stage-edge', FALLBACK.stageEdge),
    accent: token('--graph-accent', FALLBACK.accent),
    channel: token('--graph-channel', FALLBACK.channel),
    ink: token('--graph-ink', FALLBACK.ink),
    inkDim: token('--graph-ink-dim', FALLBACK.inkDim),
    inkFaint: token('--graph-ink-faint', FALLBACK.inkFaint),
    line: token('--graph-line', FALLBACK.line),
    threat: [
      token('--severity-low', FALLBACK.threat[0]),
      token('--severity-medium', FALLBACK.threat[1]),
      token('--severity-high', FALLBACK.threat[2]),
      token('--severity-critical', FALLBACK.threat[3]),
    ],
    unread: token('--graph-unread', FALLBACK.unread),
    coordinated: token('--graph-coordinated', FALLBACK.coordinated),
    supportive: token('--graph-supportive', FALLBACK.supportive),
  }
}

export const rgba = (c: Rgb, a: number) => `rgba(${c.r},${c.g},${c.b},${a})`

/**
 * The threat scale.
 *
 * `warroom_assessment.threat_score` is constrained to 1..10 in the database, and the room's
 * `deploy-threshold` sits on that same scale (7 by default). The bands are therefore derived
 * from the configured threshold rather than hard-coded: a room that moves its threshold to 5
 * has stated what it considers serious, and a fixed band table would colour the board against
 * the room's own policy.
 *
 * This mirrors `NarrativeGraphService.threatBand` exactly. If one moves, move both.
 */
export const THREAT_MAX = 10

export type ThreatBand = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'

export function threatBand(threat: number, threshold: number): ThreatBand {
  if (threat >= Math.min(THREAT_MAX, threshold + 2)) return 'CRITICAL'
  if (threat >= threshold) return 'HIGH'
  if (threat >= Math.max(2, threshold - 3)) return 'MEDIUM'
  return 'LOW'
}

/**
 * Threat → colour.
 *
 * `null` is NOT zero. A thread nothing has read returns the unread grey, and every caller that
 * wants a number out of this has to decide what to do about that case explicitly.
 */
export function threatColour(
  p: Palette, threat: number | null | undefined, threshold: number,
): Rgb {
  if (threat === null || threat === undefined) return p.unread
  switch (threatBand(threat, threshold)) {
    case 'CRITICAL': return p.threat[3]
    case 'HIGH': return p.threat[2]
    case 'MEDIUM': return p.threat[1]
    default: return p.threat[0]
  }
}

/**
 * The colour a narrative node carries.
 *
 * Coordinated narratives get their own hue rather than a place on the threat ramp, because
 * "the same sentence from forty accounts" is a different KIND of fact from "the council scored
 * this 80" — collapsing them onto one scale would let a reader mistake volume for menace.
 */
export function narrativeColour(
  p: Palette, kind: string, threat: number | null | undefined, threshold: number,
): Rgb {
  if (kind === 'COORDINATED') return p.coordinated
  if (threat === null || threat === undefined) return p.unread
  return threatColour(p, threat, threshold)
}

// ── the same bands, as CSS variables for the DOM ─────────────────────────────

/**
 * Rail and panel colours for a threat band.
 *
 * These live beside the canvas palette rather than in the components that use them, and that is
 * deliberate: a module that exports both React components and plain helpers cannot Fast Refresh,
 * so every edit to it forces a full page reload and can leave a long-lived dev tab wedged. The
 * rails file exports components only, for that reason.
 *
 * They resolve to the same four `--severity-*` steps the canvas rasterises, so a dot in the rail
 * and the node it points at can never disagree.
 */
export const SEVERITY_INK: Record<string, string> = {
  CRITICAL: 'var(--severity-critical)',
  HIGH: 'var(--severity-high)',
  MEDIUM: 'var(--severity-medium)',
  LOW: 'var(--severity-low)',
}

/** `null` is never a band — it is the unread grey, outside the ramp. */
export function threatInk(threat: number | null | undefined, threshold: number): string {
  if (threat === null || threat === undefined) return 'var(--graph-unread)'
  return SEVERITY_INK[threatBand(threat, threshold)] ?? 'var(--severity-low)'
}

/** A score, always against its scale — "8" alone invites a reader to assume out of 100. */
export function threatText(threat: number | null | undefined): string {
  return threat === null || threat === undefined ? 'Not read' : `${threat}/${THREAT_MAX}`
}
